import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MessageSquare, Send, X, Loader2, Sparkles } from "lucide-react";
import api, { API } from "@/lib/api";
import { renderMarkdown, extractUrls } from "@/lib/format.jsx";

const SESSION_KEY = "thn_chat_session";

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || null);
  const [messages, setMessages] = useState(() => [
    {
      role: "assistant",
      content:
        "Hi! I'm Zero — ZeroLabs Digitals' AI assistant. Ask me anything about AI Automation, our products, or how we can help your business.",
    },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const scrollRef = useRef(null);
  const messageRefs = useRef({});
  const abortRef = useRef(null);

  // Scroll behaviour: only fire when the message COUNT changes (i.e. a new
  // bubble is added). Streaming tokens don't change the count, so the panel
  // stays pinned at the top of the current assistant reply — visitors can
  // start reading immediately without chasing the text.
  useEffect(() => {
    if (!open) return;
    const last = messages.length - 1;
    const el = messageRefs.current[last];
    if (!el) return;
    // User message → scroll it fully into view (top of its bubble to top of panel).
    // Assistant "thinking" bubble → same behaviour so the reply starts at top.
    el.scrollIntoView({ block: "start", behavior: "smooth" });
  }, [open, messages.length]);

  // Stream chat replies via SSE (fetch + ReadableStream)
  const send = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setStreaming(true);
    setMessages((m) => [...m, { role: "user", content: text }, { role: "assistant", content: "" }]);
    setInput("");

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const token = localStorage.getItem("thn_token");
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: "include",
        body: JSON.stringify({ session_id: sessionId, message: text }),
        signal: controller.signal,
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        // SSE events separated by blank lines
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const part of parts) {
          const lines = part.split("\n");
          const evLine = lines.find((l) => l.startsWith("event: "));
          const dataLine = lines.find((l) => l.startsWith("data: "));
          if (!dataLine) continue;
          const evt = evLine ? evLine.slice(7).trim() : "message";
          let data = {};
          try { data = JSON.parse(dataLine.slice(6)); } catch { /* noop */ }

          if (evt === "session" && data.session_id) {
            setSessionId(data.session_id);
            localStorage.setItem(SESSION_KEY, data.session_id);
          } else if (evt === "delta" && typeof data.text === "string") {
            setMessages((m) => {
              const last = m[m.length - 1];
              if (!last || last.role !== "assistant") return m;
              const copy = [...m];
              copy[copy.length - 1] = { ...last, content: last.content + data.text };
              return copy;
            });
          } else if (evt === "error") {
            setMessages((m) => {
              const last = m[m.length - 1];
              if (!last || last.role !== "assistant") return m;
              const copy = [...m];
              copy[copy.length - 1] = { ...last, content: "Sorry — I hit a snag. Please try again." };
              return copy;
            });
          }
        }
      }
    } catch (e) {
      setMessages((m) => {
        const last = m[m.length - 1];
        if (!last || last.role !== "assistant" || last.content) return m;
        const copy = [...m];
        copy[copy.length - 1] = { ...last, content: "Sorry — I hit a snag. Please try again." };
        return copy;
      });
    } finally {
      setStreaming(false);
      abortRef.current = null;
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const suggestions = [
    "What AI Automation services do you offer?",
    "Tell me about your products",
    "I want to book a free consultation",
  ];

  const node = (
    <>
      {!open && (
        <button
          data-testid="chat-launcher"
          onClick={() => setOpen(true)}
          className="chat-pulse fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-[#22D3EE] text-[#08060F] flex items-center justify-center shadow-2xl hover:scale-105 transition-transform"
          aria-label="Open chat"
        >
          <MessageSquare size={22} />
        </button>
      )}

      {open && (
        <div
          data-testid="chat-panel"
          className="fixed bottom-6 right-6 z-50 w-[92vw] max-w-[400px] h-[600px] max-h-[80vh] flex flex-col glass rounded-2xl overflow-hidden shadow-2xl"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#22D3EE] to-[#3B82F6] flex items-center justify-center text-[#08060F]">
                <Sparkles size={16} />
              </div>
              <div className="leading-tight">
                <div className="text-white font-semibold text-sm">Zero</div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#22D3EE]">AI Assistant</div>
              </div>
            </div>
            <button data-testid="chat-close" onClick={() => setOpen(false)} className="text-[#94A3B8] hover:text-white p-1" aria-label="Close chat">
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-4 space-y-3 scroll-smooth">
            {messages.map((m, i) => (
              <div
                key={i}
                ref={(el) => { if (el) messageRefs.current[i] = el; }}
                className="space-y-2 scroll-mt-2"
              >
                <div
                  className={`max-w-[88%] px-3 py-2 rounded-2xl text-sm leading-relaxed ${
                    m.role === "user"
                      ? "ml-auto bg-[#3B82F6] text-[#08060F] rounded-br-sm whitespace-pre-wrap"
                      : "bg-white/5 border border-white/10 text-white rounded-bl-sm"
                  }`}
                  data-testid={`chat-msg-${m.role}-${i}`}
                >
                  {m.content ? (
                    m.role === "assistant" ? renderMarkdown(m.content) : m.content
                  ) : (
                    streaming && i === messages.length - 1 ? (
                      <span className="inline-flex items-center gap-1 text-[#94A3B8]">
                        <Loader2 size={12} className="animate-spin" /> thinking
                      </span>
                    ) : null
                  )}
                </div>
                {m.role === "assistant" && extractUrls(m.content).length > 0 && (
                  <LinkCards urls={extractUrls(m.content)} />
                )}
              </div>
            ))}
            {messages.length === 1 && (
              <div className="pt-2 flex flex-wrap gap-2">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => setInput(s)}
                    className="text-xs px-3 py-1.5 rounded-full border border-white/15 text-[#94A3B8] hover:text-[#22D3EE] hover:border-[#22D3EE] transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="border-t border-white/10 p-3 flex items-end gap-2">
            <textarea
              data-testid="chat-input"
              rows={1}
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKey}
              placeholder="Ask Zero anything…"
              className="input-field !py-2 !px-3 text-sm flex-1 min-h-[40px] !rounded-xl resize-none"
            />
            <button
              data-testid="chat-send"
              onClick={send}
              disabled={streaming || !input.trim()}
              className="btn-primary !py-2 !px-3 disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Send"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );

  if (typeof document === "undefined") return null;
  return createPortal(node, document.body);
}

function LinkCards({ urls }) {
  return (
    <div className="flex flex-wrap gap-2">
      {urls.map((u) => (
        <a
          key={u}
          href={u}
          target="_blank"
          rel="noreferrer"
          className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#22D3EE]/10 text-[#22D3EE] border border-[#22D3EE]/30 hover:bg-[#22D3EE]/20 transition-colors"
          data-testid="chat-link-card"
        >
          Open: {new URL(u).hostname.replace(/^www\./, "")}
        </a>
      ))}
    </div>
  );
}
